import { NG_VALUE_ACCESSOR } from '@angular/forms';
import * as i0 from '@angular/core';
import { forwardRef, EventEmitter, Component, ViewEncapsulation, Input, Output, HostListener, NgModule } from '@angular/core';
import * as KeyCodes from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

const noop = () => {
};
const UX_SWITCH_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => UxSwitchComponent),
    multi: true,
};
class UxSwitchComponent {
    constructor() {
        this._isChecked = false;
        this._isDisabled = false;
        this._isSmall = false;
        this._isLarge = false;
        this.label = 'Toggle Button';
        this.tabindex = '0';
        this.e2eAttr = 'ux-switch';
        this.toggle = new EventEmitter();
        this.onChange = noop;
        this.onTouched = noop;
    }
    get isChecked() { return this._isChecked; }
    set isChecked(value) { this._isChecked = coerceBooleanProperty(value); }
    get isDisabled() { return this._isDisabled; }
    set isDisabled(value) { this._isDisabled = coerceBooleanProperty(value); }
    get isSmall() { return this._isSmall; }
    set isSmall(value) { this._isSmall = coerceBooleanProperty(value); }
    get isLarge() { return this._isLarge; }
    set isLarge(value) { this._isLarge = coerceBooleanProperty(value); }
    handleKey(evt) {
        // eslint-disable-next-line import/no-deprecated
        if (evt.which === KeyCodes.SPACE) {
            this.handleClicked();
        }
    }
    handleClicked() {
        if (this.isDisabled) {
            return;
        }
        this.isChecked = !this.isChecked;
        this.onChange(this.isChecked);
        this.onTouched(this.isChecked);
        this.toggle.emit(this.isChecked);
    }
    writeValue(obj) {
        this.isChecked = obj;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.isDisabled = isDisabled;
    }
}
/** @nocollapse */ UxSwitchComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxSwitchComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ UxSwitchComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: UxSwitchComponent, selector: "ux-switch", inputs: { styleClass: "styleClass", isChecked: "isChecked", isDisabled: "isDisabled", isSmall: "isSmall", isLarge: "isLarge", label: "label", tabindex: "tabindex", e2eAttr: "e2eAttr" }, outputs: { toggle: "toggle" }, host: { listeners: { "keyup": "handleKey($event)", "click": "handleClicked()" } }, providers: [UX_SWITCH_VALUE_ACCESSOR], ngImport: i0, template: `
        <span
            class="ux-c-switch {{styleClass}}"
            [class.ux-c-switch--small]="isSmall"
            [class.ux-c-switch--large]="isLarge"
            [class.ux-c-switch--checked]="isChecked"
            [class.ux-c-switch--disabled]="isDisabled"
            role="switch"
            [tabindex]="tabindex"
            [attr.aria-label]="label"
            [attr.aria-checked]="isChecked"
            [attr.aria-disabled]="isDisabled"
            attr.data-e2e="{{ e2eAttr }}">
          <small class="ux-c-switch__handle"></small>
        </span>
    `, isInline: true, styles: [".ux-c-switch{background:var(--eui-base-color-white);background-clip:content-box;border:1px solid var(--eui-base-color-grey-15);border-radius:1.25em;box-shadow:#dfdfdf 0 0 inset;box-sizing:content-box;cursor:pointer;display:inline-block;font-size:var(--eui-base-font-size-l);height:var(--eui-base-spacing-l);margin:0;overflow:visible;padding:0;position:relative;transition:.3s ease-out all;-webkit-user-select:none;user-select:none;vertical-align:middle;width:var(--eui-base-spacing-2xl)}.ux-c-switch:focus:active:not([readonly]),.ux-c-switch:focus:focus:not([readonly]){z-index:auto;border-color:var(--eui-base-color-accessible-focus)!important;box-shadow:inset 0 0 0 1px var(--eui-base-color-accessible-focus),inset 0 0 0 2px var(--eui-base-color-grey-5);transition:border-color .15s ease-in-out,box-shadow .15s ease-in-out}.ux-c-switch--large{transform:scale(1.5)}.ux-c-switch--small{transform:scale(.75)}.ux-c-switch--tiny{transform:scale(.6)}.ux-c-switch--checked{background:var(--eui-base-color-success-75);border-color:var(--eui-base-color-success-75)}.ux-c-switch--checked .ux-c-switch__handle{left:var(--eui-base-spacing-m)}.ux-c-switch--disabled{background-image:none;box-shadow:none;cursor:not-allowed;opacity:var(--eui-base-disabled-opacity)}.ux-c-switch__handle{background-color:var(--eui-base-color-white);border-radius:100%;font-size:var(--eui-base-font-size);height:var(--eui-base-spacing-l);left:0;position:absolute;top:0;transition:.3s ease-out all;width:var(--eui-base-spacing-l);box-shadow:var(--eui-base-shadow-2)}.material-switch>input[type=checkbox]{display:none}.material-switch>label{cursor:pointer!important;height:0;position:relative!important;width:40px}.material-switch>label:before{background:rgb(0,0,0);border-radius:8px;box-shadow:inset 0 0 10px #00000080;content:\"\";height:16px;margin-top:-8px;opacity:.3;position:absolute;transition:all .4s ease-in-out;width:40px}.material-switch>label:after{background:rgb(255,255,255);border-radius:16px;box-shadow:0 0 5px #0000004d;content:\"\";height:24px;left:-4px;margin-top:-8px;position:absolute;top:-4px;transition:all .3s ease-in-out;width:24px}.material-switch>input[type=checkbox]:checked+label:before{background:inherit;opacity:.5}.material-switch>input[type=checkbox]:checked+label:after{background:inherit;left:20px}\n"], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxSwitchComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ux-switch', template: `
        <span
            class="ux-c-switch {{styleClass}}"
            [class.ux-c-switch--small]="isSmall"
            [class.ux-c-switch--large]="isLarge"
            [class.ux-c-switch--checked]="isChecked"
            [class.ux-c-switch--disabled]="isDisabled"
            role="switch"
            [tabindex]="tabindex"
            [attr.aria-label]="label"
            [attr.aria-checked]="isChecked"
            [attr.aria-disabled]="isDisabled"
            attr.data-e2e="{{ e2eAttr }}">
          <small class="ux-c-switch__handle"></small>
        </span>
    `, providers: [UX_SWITCH_VALUE_ACCESSOR], encapsulation: ViewEncapsulation.None, styles: [".ux-c-switch{background:var(--eui-base-color-white);background-clip:content-box;border:1px solid var(--eui-base-color-grey-15);border-radius:1.25em;box-shadow:#dfdfdf 0 0 inset;box-sizing:content-box;cursor:pointer;display:inline-block;font-size:var(--eui-base-font-size-l);height:var(--eui-base-spacing-l);margin:0;overflow:visible;padding:0;position:relative;transition:.3s ease-out all;-webkit-user-select:none;user-select:none;vertical-align:middle;width:var(--eui-base-spacing-2xl)}.ux-c-switch:focus:active:not([readonly]),.ux-c-switch:focus:focus:not([readonly]){z-index:auto;border-color:var(--eui-base-color-accessible-focus)!important;box-shadow:inset 0 0 0 1px var(--eui-base-color-accessible-focus),inset 0 0 0 2px var(--eui-base-color-grey-5);transition:border-color .15s ease-in-out,box-shadow .15s ease-in-out}.ux-c-switch--large{transform:scale(1.5)}.ux-c-switch--small{transform:scale(.75)}.ux-c-switch--tiny{transform:scale(.6)}.ux-c-switch--checked{background:var(--eui-base-color-success-75);border-color:var(--eui-base-color-success-75)}.ux-c-switch--checked .ux-c-switch__handle{left:var(--eui-base-spacing-m)}.ux-c-switch--disabled{background-image:none;box-shadow:none;cursor:not-allowed;opacity:var(--eui-base-disabled-opacity)}.ux-c-switch__handle{background-color:var(--eui-base-color-white);border-radius:100%;font-size:var(--eui-base-font-size);height:var(--eui-base-spacing-l);left:0;position:absolute;top:0;transition:.3s ease-out all;width:var(--eui-base-spacing-l);box-shadow:var(--eui-base-shadow-2)}.material-switch>input[type=checkbox]{display:none}.material-switch>label{cursor:pointer!important;height:0;position:relative!important;width:40px}.material-switch>label:before{background:rgb(0,0,0);border-radius:8px;box-shadow:inset 0 0 10px #00000080;content:\"\";height:16px;margin-top:-8px;opacity:.3;position:absolute;transition:all .4s ease-in-out;width:40px}.material-switch>label:after{background:rgb(255,255,255);border-radius:16px;box-shadow:0 0 5px #0000004d;content:\"\";height:24px;left:-4px;margin-top:-8px;position:absolute;top:-4px;transition:all .3s ease-in-out;width:24px}.material-switch>input[type=checkbox]:checked+label:before{background:inherit;opacity:.5}.material-switch>input[type=checkbox]:checked+label:after{background:inherit;left:20px}\n"] }]
        }], propDecorators: { styleClass: [{
                type: Input
            }], isChecked: [{
                type: Input
            }], isDisabled: [{
                type: Input
            }], isSmall: [{
                type: Input
            }], isLarge: [{
                type: Input
            }], label: [{
                type: Input
            }], tabindex: [{
                type: Input
            }], e2eAttr: [{
                type: Input
            }], toggle: [{
                type: Output
            }], handleKey: [{
                type: HostListener,
                args: ['keyup', ['$event']]
            }], handleClicked: [{
                type: HostListener,
                args: ['click']
            }] } });
class UxSwitchComponentModule {
}
/** @nocollapse */ UxSwitchComponentModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxSwitchComponentModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ UxSwitchComponentModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: UxSwitchComponentModule, declarations: [UxSwitchComponent], imports: [CommonModule], exports: [UxSwitchComponent] });
/** @nocollapse */ UxSwitchComponentModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxSwitchComponentModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxSwitchComponentModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    exports: [UxSwitchComponent],
                    declarations: [UxSwitchComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { UX_SWITCH_VALUE_ACCESSOR, UxSwitchComponent, UxSwitchComponentModule };
